import {
	CreateLobbyDto,
	GetLobbiesBySlateDto,
	InitiateRevivalDto,
	JoinLobbyDto,
	UpdateLobbyDto,
	UpdateLobbyPriorityDto,
	UpdateLobbyUsersDto,
	UpdateRevivalCountDto,
} from '@sportspredict/shared';
import api from './base-api';

const lobbiesApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// PUBLIC
		getLobby: builder.query({
			query: (id: string) => ({
				url: `lobbies/${id}`,
				method: 'GET',
			}),
			providesTags: (_result, _error, id: string) => [{ type: 'Lobby', id }],
			extraOptions: { skipAuth: true },
		}),
		getLobbiesByEventId: builder.query({
			query: (eventId: string) => ({
				url: `lobbies/event/${eventId}`,
				method: 'GET',
			}),
			providesTags: [{ type: 'Lobby', id: 'LIST' }],
			extraOptions: { skipAuth: true },
		}),
		joinLobby: builder.mutation<unknown, JoinLobbyDto>({
			query: (data) => ({
				url: 'lobbies/join',
				method: 'POST',
				body: data,
			}),
			invalidatesTags: (_result, _error, { id }) => [
				{ type: 'Lobby', id },
				{ type: 'Lobby', id: 'LIST' },
			],
		}),
		createLobby: builder.mutation({
			query: (data: CreateLobbyDto) => ({
				url: 'lobbies',
				method: 'POST',
				body: data,
			}),
			invalidatesTags: [{ type: 'Lobby', id: 'LIST' }],
		}),
		updateLobby: builder.mutation({
			query: ({ id, ...data }: UpdateLobbyDto) => ({
				url: 'lobbies',
				method: 'PUT',
				body: { id, ...data },
			}),
			invalidatesTags: (_result, _error, { id }) => [
				{ type: 'Lobby', id },
				{ type: 'Lobby', id: 'LIST' },
			],
		}),
		deleteLobby: builder.mutation({
			query: (id: string) => ({
				url: `lobbies/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: (_result, _error, id) => [
				{ type: 'Lobby', id },
				{ type: 'Lobby', id: 'LIST' },
			],
		}),
		deleteUserFromLobby: builder.mutation({
			query: (id: string) => ({
				url: `lobbies/lobby-users/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [{ type: 'Lobby', id: 'LIST' }],
		}),
		updateLobbyUsers: builder.mutation<unknown, { lobbyId: string; body: UpdateLobbyUsersDto }>({
			query: ({ lobbyId, body }) => ({
				url: `lobbies/lobby-users/${lobbyId}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: (_result, _error, { lobbyId }) => [{ type: 'Lobby', id: lobbyId }],
		}),
		getLobbiesStats: builder.query({
			query: () => ({ url: 'lobbies/lobby-stats', method: 'GET' }),
			providesTags: [{ type: 'Lobby', id: 'STATS' }],
			extraOptions: { skipAuth: true },
		}),
		getMyLobbiesStats: builder.query({
			query: () => ({ url: 'lobbies/lobby-stats/me', method: 'GET' }),
			providesTags: [{ type: 'Lobby', id: 'MY_STATS' }],
		}),
		getEliminatedUsersCount: builder.query({
			query: (lobbyId: any) => ({
				url: `/lobbies/${lobbyId}/eliminated-users/count`,
				method: 'GET',
			}),
			providesTags: (_result, _error, lobbyId: any) => [
				{ type: 'Lobby', id: `ELIMINATED_COUNT_${lobbyId}` },
			],
			extraOptions: { skipAuth: true },
		}),
		initiateRevival: builder.mutation<unknown, { lobbyId: string } & InitiateRevivalDto>({
			query: ({ lobbyId, reviveCount }) => ({
				url: `/lobbies/${lobbyId}/initiate-revival`,
				method: 'POST',
				body: { reviveCount },
			}),
			invalidatesTags: (_result, _error, { lobbyId }) => [
				{ type: 'Lobby', id: `ELIMINATED_COUNT_${lobbyId}` },
				{ type: 'Lobby', id: `REVIVAL_SUGGESTION_${lobbyId}` },
			],
			extraOptions: { skipAuth: true },
		}),
		getStationLobbies: builder.query({
			query: (slug: any) => ({
				url: `lobbies/sponsor/${slug}`,
			}),
			extraOptions: { skipAuth: true },
		}),
		getLeaderboard: builder.query({
			query: (data: any) => ({
				url: '/lobbies/leaderboard',
				method: 'POST',
				body: data,
			}),
			providesTags: (_result, _error, { lobbyId }: any) => [
				{ type: 'Lobby', id: `LEADERBOARD_${lobbyId}` },
			],
			extraOptions: { skipAuth: true },
		}),

		// PRIVATE
		getSurvivorIntro: builder.query({
			query: () => `/lobbies/survivor-intro`,
		}),
		getUserLeaderboardRank: builder.query({
			query: (data: any) => ({
				url: '/lobbies/leaderboard/rank',
				method: 'POST',
				body: data,
			}),
			providesTags: (_result, _error, { lobbyId }: any) => [
				{ type: 'Lobby', id: `USER_RANK_${lobbyId}` },
			],
		}),

		// ADMIN
		updateRevivalCount: builder.mutation<unknown, { lobbyId: string } & UpdateRevivalCountDto>({
			query: ({ lobbyId, revivalCount }) => ({
				url: `/lobbies/${lobbyId}/revival-count`,
				method: 'PATCH',
				body: { revivalCount },
			}),
		}),
		getRevivalSuggestion: builder.query({
			query: (lobbyId: any) => ({
				url: `/lobbies/${lobbyId}/revival-suggestion`,
				method: 'GET',
			}),
			providesTags: (_result, _error, lobbyId: any) => [
				{ type: 'Lobby', id: `REVIVAL_SUGGESTION_${lobbyId}` },
			],
		}),
		updateLobbyPriority: builder.mutation<any, { lobbyId: string } & UpdateLobbyPriorityDto>({
			query: ({ lobbyId, priority }) => ({
				url: `lobbies/${lobbyId}/priority`,
				method: 'PATCH',
				body: { priority },
			}),
			invalidatesTags: (_result, _error, { lobbyId }) => [
				{ type: 'Lobby', id: lobbyId },
				{ type: 'Lobby', id: 'LIST' },
			],
		}),
		getLobbiesBySlateId: builder.query<any, GetLobbiesBySlateDto>({
			query: (body) => ({
				url: `lobbies/by-slate`,
				method: 'POST',
				body,
			}),
			providesTags: [{ type: 'Lobby', id: 'LIST' }],
		}),
		getLobbiesByEventIdForAdmin: builder.query({
			query: (eventId: string) => ({
				url: `lobbies/event/${eventId}/admin`,
				method: 'GET',
			}),
			providesTags: (_result, _error, eventId: string) => [
				{ type: 'Lobby', id: `EVENT_ADMIN_${eventId}` },
			],
		}),
	}),
	overrideExisting: false,
});

export default lobbiesApi;

export const {
	useGetLobbyQuery,
	useLazyGetLobbyQuery,
	useGetLobbiesByEventIdQuery,
	useLazyGetLobbiesByEventIdQuery,
	useJoinLobbyMutation,
	useCreateLobbyMutation,
	useUpdateLobbyMutation,
	useDeleteLobbyMutation,
	useDeleteUserFromLobbyMutation,
	useUpdateLobbyUsersMutation,
	useGetLobbiesStatsQuery,
	useLazyGetLobbiesStatsQuery,
	useGetMyLobbiesStatsQuery,
	useLazyGetMyLobbiesStatsQuery,
	useGetEliminatedUsersCountQuery,
	useInitiateRevivalMutation,
	useUpdateRevivalCountMutation,
	useGetRevivalSuggestionQuery,
	useGetStationLobbiesQuery,
	useGetSurvivorIntroQuery,
	useUpdateLobbyPriorityMutation,
	useGetLeaderboardQuery,
	useGetUserLeaderboardRankQuery,
	useGetLobbiesBySlateIdQuery,
	useGetLobbiesByEventIdForAdminQuery,
	useLazyGetLobbiesByEventIdForAdminQuery,
} = lobbiesApi;
